import jwt from 'jsonwebtoken';
import {DateTime} from 'luxon';

export const DEFAULT_SECONDS_BEFORE_EXPIRY_WARN = 180;
const DEFAULT_ONE_MINUTE_IN_MILLISECONDS = 60;

export const DEFAULT_CALLBACKS = {
  // Called when the token could not be decoded
  onErrorDecode: () => {},
  // Called when there was an error parsing the expiry time
  onErrorParseExpiry: () => {},
  // Called if the token is expired
  onTokenExpired: () => {},
  // Called if the token is within the expiry warning period
  onTokenExpiryWarning: () => {},
};

/**
 * Util provider for JWT tokens.
 *
 * To implement typically an application would:
 * - Define useful callback actions
 * - Export an instantiation of the token provider
 * - Set a token at any application appropriate time (eg, new auth0 access token fetches)
 *
 * Class definitions:
 * - {String} token The raw token given to the provider
 * - {Object} tokenPayload The decoded token payload
 * - {SetTimeout} expiryWarnTimeout Timeout triggering when warning about the upcoming token expiry
 * - {SetTimeout} expiredTimeout Timeout triggering when the token has expired
 * - {SetTimeout} silentRefreshTimeout Timeout triggering when the token refresh is due
 * - {Object} timeToExpiry Luxon diff from now for the time until expiry
 * - {Number} secondsBeforeExpiryWarn How many seconds prior to the token expiry should the warning callback be called
 *
 * // True if the token is expired
 * isExpired = false;
 * isWarned = false;
 */
export default class {
  /**
   * @param {Object} callbacks Functions called during specific events an application can hook into
   * @param {String} token The raw JWT token
   * @param {Number} secondsBeforeExpiryWarn The amount of seconds before a token has expired that should trigger a warning
   * @param {Number} secondsToExtendSession The amount of seconds before a token will be refreshed to extend its expiry
   */
  constructor(
    callbacks = DEFAULT_CALLBACKS,
    {
      token = '',
      secondsBeforeExpiryWarn = DEFAULT_SECONDS_BEFORE_EXPIRY_WARN,
      secondsToExtendSession = DEFAULT_ONE_MINUTE_IN_MILLISECONDS,
    } = {},
  ) {
    this.resetToken();
    this.resetExpiry();
    this.secondsBeforeExpiryWarn = secondsBeforeExpiryWarn;
    this.secondsToExtendSession = secondsToExtendSession;
    this.isSilentRefreshReady = true;

    this.callbacks = {
      ...DEFAULT_CALLBACKS,
      ...callbacks,
    };

    if (token) {
      this.setToken(token);
    }
  }

  /**
   * Clears the expiry timeouts to prevent incorrect callbacks
   */
  clearTimeouts() {
    clearTimeout(this.expiredTimeout);
    this.expiredTimeout = undefined;
    clearTimeout(this.expiryWarnTimeout);
    this.expiryWarnTimeout = undefined;
    clearTimeout(this.silentRefreshTimeout);
    this.silentRefreshTimeout = undefined;
  }

  /**
   * Clears expiry timeouts and all stored expiry information from the last setTokenExpiry
   */
  resetExpiry() {
    this.clearTimeouts();
    this.isExpired = false;
    this.isWarned = false;
    this.timeToExpiry = {};
  }

  /**
   * Clears token information
   */
  resetToken() {
    this.token = '';
    this.tokenPayload = {};
  }

  /**
   * Stores a given token and its decoded payload
   *
   * @param {String} token The JWT token to set and decode
   */
  setToken(token) {
    // Clear the timeouts as they will be set to the new token expiry
    this.resetExpiry();

    this.isError = false;
    this.token = token;
    this.tokenPayload = jwt.decode(token);

    // Callback if unable to decode
    if (!this.tokenPayload) {
      this.isError = true;
      this.callbacks.onErrorDecode();

      return;
    }

    this.setTokenExpiry();
  }

  /**
   * Stores expiry information from the last setToken and sets timeouts that trigger callbacks when the time
   * gets close to or passes the tokens expiry
   *
   * @param {Number} referenceFromEpoch Seconds to use as a reference time to diff against the tokens expiry
   */
  setTokenExpiry(referenceFromEpoch) {
    if (!this.isError) {
      try {
        if (!this.tokenPayload.exp) {
          throw new Error('No expiry on the token, can not set expiry time');
        }

        this.isSilentRefreshReady = false;
        this.silentRefreshTimeout = setTimeout(() => {
          this.isSilentRefreshReady = true;
        }, this.secondsToExtendSession * 1000);

        // Default the reference date time to now. A value can be given if referencing expiry from a specific time, eg, testing.
        const refDT = referenceFromEpoch ? DateTime.fromSeconds(referenceFromEpoch) : DateTime.now();

        // Determine the time from now until the token is expired
        this.timeToExpiry = DateTime.fromSeconds(this.tokenPayload.exp).diff(refDT, ['seconds']).values;

        // Callback if the token is already expired or set the timeout to call the callback when it is expired
        if (this.timeToExpiry.seconds < 0) {
          this.callbacks.onTokenExpired();
          this.isExpired = true;

          return;
        } else {
          this.expiredTimeout = setTimeout(() => {
            this.isExpired = true;
            this.callbacks.onTokenExpired();
          }, this.timeToExpiry.seconds * 1000);
        }

        // Determine the seconds until the expiry warning callback should be called
        const secondsWarningDiff = this.timeToExpiry.seconds - this.secondsBeforeExpiryWarn;

        // Set the timeout to call the expiry warning callback
        if (secondsWarningDiff < 0) {
          this.isWarned = true;
        } else {
          this.expiryWarnTimeout = setTimeout(() => {
            this.isWarned = true;
            this.callbacks.onTokenExpiryWarning();
          }, secondsWarningDiff * 1000);
        }
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);

        // Reset all expiry data and call the expiry parse error callback if there were any errors
        this.resetExpiry();
        this.isError = true;
        this.callbacks.onErrorParseExpiry(e);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/features/login/jwt-token-provider/lib.js